import { useMemo, useState } from 'react'
import { ListFilter } from 'lucide-react'

import type { FilterDefinition } from '@/components/filters/list-filter-types'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'

export type SecondaryFiltersMenuProps<TFilters, TCtx> = {
  definitions: FilterDefinition<TFilters, TCtx>[]
  visibleKeys: Set<string>
  onPickFilter: (key: string) => void
  onOpenChange?: (open: boolean) => void
  disabled?: boolean
  ariaLabel?: string
}

export function SecondaryFiltersMenu<TFilters, TCtx>({
  definitions,
  visibleKeys,
  onPickFilter,
  onOpenChange,
  disabled,
  ariaLabel = 'Filter',
}: SecondaryFiltersMenuProps<TFilters, TCtx>) {
  const [open, setOpen] = useState(false)

  const handleOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen)
    onOpenChange?.(nextOpen)
  }

  const availableDefinitions = useMemo(
    () => definitions.filter((def) => !visibleKeys.has(def.key)),
    [definitions, visibleKeys],
  )

  if (definitions.length === 0) return null

  return (
    <DropdownMenu open={open} onOpenChange={handleOpenChange}>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="ghostMuted"
          size="iconSm"
          disabled={disabled}
          aria-label={ariaLabel}
          title={ariaLabel}
        >
          <ListFilter className="h-4 w-4" aria-hidden />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="w-56"
        onCloseAutoFocus={(event) => event.preventDefault()}
      >
        {availableDefinitions.length === 0 ? (
          <p className="px-2 py-1.5 text-sm text-muted-foreground">All filters added</p>
        ) : (
          availableDefinitions.map((def) => {
            const Icon = def.icon
            return (
              <DropdownMenuItem
                key={def.key}
                className="gap-2"
                onSelect={() => {
                  onPickFilter(def.key)
                  handleOpenChange(false)
                }}
              >
                {Icon ? <Icon className="h-4 w-4 text-muted-foreground" aria-hidden /> : null}
                {def.label}
              </DropdownMenuItem>
            )
          })
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
